import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/api/client";
import { useAuthStore } from "@/store/authStore";
import toast from "react-hot-toast";
import clsx from "clsx";

const ROLE_LABELS = {
  teacher: "Lehrperson",
  parent: "Elternteil",
  mentor: "Mentor",
  admin: "Administrator",
};

const AVATAR_GRID = [
  "👨‍🏫","👩‍🏫","👨‍💼","👩‍💼","👨‍🔬","👩‍🔬","👨‍⚕️","👩‍⚕️",
  "👨‍🎓","👩‍🎓","🧑‍🏫","🧑‍💼","🦊","🐺","🦁","🐯",
  "🦅","🦋","⭐","🌟","🔥","💎","🎯","🚀",
];

// DisplayStyle picker with explanation
const DISPLAY_STYLES = [
  {
    value: "full",
    label: "Voller Name",
    example: "Maria Schneider",
    desc: "Dein vollständiger Name wird angezeigt",
    icon: "👤",
  },
  {
    value: "formal",
    label: "Formell",
    example: "Frau Schneider",
    desc: "Nur Nachname mit Anrede (für Nachrichten mit Schülern)",
    icon: "🎓",
  },
  {
    value: "anonymous",
    label: "Anonym",
    example: "Lehrperson",
    desc: "Nur deine Rolle wird angezeigt",
    icon: "🔒",
  },
];

export default function NonStudentProfile() {
  const { user, updateUser } = useAuthStore();
  const qc = useQueryClient();

  const [avatar, setAvatar] = useState(user?.avatar || "👤");
  const [displayName, setDisplayName] = useState(user?.displayName || "");
  const [displayStyle, setDisplayStyle] = useState(user?.displayStyle || "full");
  const [showAvatarPicker, setShowAvatarPicker] = useState(false);

  const save = useMutation({
    mutationFn: () => api.patch("/users/me/profile", {
      avatar,
      displayName: displayName.trim() || null,
      displayStyle,
    }),
    onSuccess: (res) => {
      if (updateUser) updateUser(res.data.user);
      else useAuthStore.setState({ user: res.data.user });
      toast.success("Profil gespeichert ✅");
    },
    onError: (err) => toast.error(err.response?.data?.error || "Fehler beim Speichern"),
  });

  if (!user || user.role === "student") return null;

  const roleLabel = ROLE_LABELS[user.role] || user.role;

  // Calculate what the display name looks like
  const getDisplayPreview = () => {
    const name = displayName.trim() || user.name;
    if (displayStyle === "full") return name;
    if (displayStyle === "formal") {
      const lastName = name.split(" ").pop();
      const prefix = user.role === "teacher" ? (name.startsWith("H") ? "Herr" : "Frau") : "";
      return prefix ? `${prefix} ${lastName}` : lastName;
    }
    return roleLabel;
  };

  return (
    <div className="space-y-5 animate-fade-in pb-8">
      {/* Header */}
      <div>
        <h1 className="text-xl font-black flex items-center gap-2">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" strokeWidth="1.8" stroke="#8b5cf6" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
          </svg>
          Mein Profil
        </h1>
        <p className="text-xs text-gray-500 mt-0.5">Passe dein Erscheinungsbild in der App an</p>
      </div>

      {/* Avatar display + picker button */}
      <div className="bg-nexus-sf border border-nexus-border rounded-2xl p-5">
        <div className="flex items-center gap-4">
          <button
            onClick={() => setShowAvatarPicker(!showAvatarPicker)}
            className="relative w-20 h-20 rounded-2xl flex items-center justify-center text-5xl transition-all hover:scale-105 active:scale-95"
            style={{
              background: "linear-gradient(135deg, rgba(139,92,246,0.15), rgba(6,182,212,0.08))",
              border: "2px solid rgba(139,92,246,0.3)",
              boxShadow: "0 0 20px rgba(139,92,246,0.15)",
            }}
          >
            {avatar}
            <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-violet-600 flex items-center justify-center border-2 border-nexus-sf">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" strokeWidth="2.5" stroke="white" strokeLinecap="round"><path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/></svg>
            </div>
          </button>
          <div>
            <p className="font-black text-lg text-white">{user.name}</p>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full"
                style={{ background: "rgba(139,92,246,0.15)", color: "#a78bfa", border: "1px solid rgba(139,92,246,0.25)" }}>
                {roleLabel}
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-1">Tippe auf den Avatar zum Ändern</p>
          </div>
        </div>

        {/* Avatar picker grid */}
        {showAvatarPicker && (
          <div className="mt-4 pt-4 border-t border-nexus-border">
            <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest mb-3">Avatar wählen</p>
            <div className="grid grid-cols-8 gap-2">
              {AVATAR_GRID.map((a) => (
                <button
                  key={a}
                  onClick={() => { setAvatar(a); setShowAvatarPicker(false); }}
                  className={clsx(
                    "w-full aspect-square rounded-xl text-2xl flex items-center justify-center transition-all hover:scale-110 active:scale-95",
                    avatar === a
                      ? "bg-violet-600/30 border border-violet-500/60"
                      : "bg-nexus-s2 border border-nexus-border hover:border-violet-500/30"
                  )}
                >
                  {a}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Display Name */}
      <div className="bg-nexus-sf border border-nexus-border rounded-2xl overflow-hidden">
        <div className="flex items-center gap-2.5 px-4 py-3 border-b border-nexus-border">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" strokeWidth="2" stroke="#6b7280" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
          <h3 className="text-xs font-black text-gray-400 uppercase tracking-widest">Anzeigename</h3>
        </div>
        <div className="px-4 py-4 space-y-3">
          <p className="text-xs text-gray-500">Optionaler Name (z.B. "Maria Schneider" oder "Frau Schneider")</p>
          <input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder={`Standard: ${user.name}`}
            maxLength={60}
            className="w-full bg-nexus-s2 border border-nexus-border rounded-xl px-4 py-2.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-violet-500 transition-colors"
          />
          {displayName.trim() && (
            <p className="text-[11px] text-gray-600">
              Vorschau: <span className="text-violet-400 font-bold">{getDisplayPreview()}</span>
            </p>
          )}
        </div>
      </div>

      {/* Display Style */}
      <div className="bg-nexus-sf border border-nexus-border rounded-2xl overflow-hidden">
        <div className="flex items-center gap-2.5 px-4 py-3 border-b border-nexus-border">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" strokeWidth="2" stroke="#6b7280" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
          <h3 className="text-xs font-black text-gray-400 uppercase tracking-widest">Wie erscheine ich in Nachrichten?</h3>
        </div>
        <div className="px-4 py-4 space-y-2.5">
          <p className="text-xs text-gray-500 mb-3">Wähle wie du in Chats für Schüler und Eltern angezeigt wirst</p>
          {DISPLAY_STYLES.map((style) => (
            <button
              key={style.value}
              onClick={() => setDisplayStyle(style.value)}
              className={clsx(
                "w-full flex items-center gap-3 px-4 py-3 rounded-xl border text-left transition-all",
                displayStyle === style.value
                  ? "border-violet-500/50 bg-violet-900/20"
                  : "border-nexus-border bg-nexus-s2 hover:border-violet-500/20"
              )}
            >
              <span className="text-2xl flex-shrink-0">{style.icon}</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-white">{style.label}</p>
                <p className="text-[10px] text-gray-500 mt-0.5">{style.desc}</p>
                <p className="text-[11px] text-violet-400 font-bold mt-0.5">z.B. "{style.example}"</p>
              </div>
              {displayStyle === style.value && (
                <div className="w-5 h-5 rounded-full bg-violet-600 flex items-center justify-center flex-shrink-0">
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" strokeWidth="3" stroke="white" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
                </div>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Preview box */}
      <div className="rounded-xl px-4 py-3" style={{ background: "rgba(6,182,212,0.05)", border: "1px solid rgba(6,182,212,0.15)" }}>
        <p className="text-[10px] font-black text-cyan-500 uppercase tracking-widest mb-1.5">Vorschau in Nachrichten</p>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-nexus-s2 border border-nexus-border flex items-center justify-center text-xl flex-shrink-0">
            {avatar}
          </div>
          <div>
            <p className="text-sm font-black text-white">{getDisplayPreview()}</p>
            <p className="text-[10px] text-gray-600">{roleLabel}</p>
          </div>
        </div>
      </div>

      {/* Save button */}
      <button
        onClick={() => save.mutate()}
        disabled={save.isPending}
        className="w-full py-3.5 rounded-2xl font-black text-sm text-white transition-all duration-200 active:scale-[0.98] disabled:opacity-70"
        style={{ background: "linear-gradient(135deg, #7c3aed, #5b21b6)", boxShadow: "0 4px 20px rgba(124,58,237,0.35)" }}
      >
        {save.isPending ? (
          <div className="flex items-center justify-center gap-2">
            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"/>
            Speichern…
          </div>
        ) : "Profil speichern"}
      </button>
    </div>
  );
}
